import React from 'react';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { faCheckCircle, faScrewdriver } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    onFieldChange, onReactSelectChanged, ToastMessage,
    Input,
    FormCheckbox,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';

export class JobTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const initialJobType = this.props.jobType ?? {};
        const stateBase = {
            jobType: initialJobType,
            originalData: {
                ...clone(initialJobType),
            },
            show: false,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    onChange = onFieldChange.bind(this);

    onClose(response) {
        const hasUnsavedChanges =
            !isEqual(this.state.originalData, {
                ...this.state.jobType,
            });

        if (hasUnsavedChanges) {
            if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                return;
            }
        }

        this.setState({ show: false });
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onSelectChange = onReactSelectChanged.bind(this);

    onSubmit = async () => {
        const { jobType } = this.state;

        // Clear any fluent api errors
        this.setState({ errors: {}, saving: true });

        try {
            const response = await httpClient.put(ApiRoutes.jobType.update(jobType.id), jobType);

            if (response) {
                // Show success notification
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message={`Job Type [${jobType.description}] saved.`}
                    />,
                );

                // Reset the form state and close the form
                this.setState({ show: false, saving: false });
                this.context.setFormOpened(false);
                this.resetForm();
                this.props.onSaveCallback(response);
            }
        } catch (err) {
            this.handleSaveError(err);
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = (jobType) => {
        this.resetForm();
        this.context.setFormOpened(true);

        this.setState({
            show: true,
            jobType,
            originalData: {
                ...clone(jobType),
            },
            loading: false,
        });
    };

    resetForm() {
        this.setState({ formValidated: false });
    }

    render() {
        const {
            formValidated, validationMessage, originalData, jobType,
        } = this.state;


        const hasUnsavedChanges = !isEqual(originalData, {
            ...jobType,
        });

        if (!this.context?.tenant?.tenantSettings) {
            return null;
        }

        return (
            <>
                <FormPrompt
                    when={hasUnsavedChanges}
                    message="You have unsaved changes. Are you sure you want to leave?"
                />

                <SlideForm
                    loading={this.state.loading}
                    show={this.state.show}
                    id="jobTypeForm"
                    formIcon={faScrewdriver}
                    formTitle={`Edit Job Type ${jobType.description}`}
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    onSave={this.onSave}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    size="col-xl-5"
                >
                    <Input
                        label="Display Color"
                        id="displayColor"
                        name="jobType.displayColor"
                        type="color"
                        onChange={this.onChange}
                        value={jobType.displayColor || '#9ca3af'}
                    />

                    <Input
                        label="Invoice Description"
                        id="invoiceDescription"
                        name="jobType.invoiceDescription"
                        value={jobType.invoiceDescription || ''}
                        onChange={this.onChange}
                        placeholder="Enter invoice description"
                    />

                    <FormCheckbox
                        className="ml-3 mt-2"
                        id="timesheetRequiresWorkflowNotes"
                        name="jobType.timesheetRequiresWorkflowNotes"
                        checked={jobType.timesheetRequiresWorkflowNotes || false}
                        onChange={this.onChange}
                        labelText="Timesheet setup charge details require additional notes"
                    />
                </SlideForm>
            </>
        );
    }
}
